import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tag, MessageSquare, Mail, Phone, Clock, Plus } from "lucide-react";
import api from "@/lib/api";

const FALLBACK_COLORS = [
    "bg-emerald-100 text-emerald-800 border-emerald-300",
    "bg-blue-100 text-blue-800 border-blue-300",
    "bg-indigo-100 text-indigo-800 border-indigo-300",
    "bg-purple-100 text-purple-800 border-purple-300",
    "bg-rose-100 text-rose-800 border-rose-300",
    "bg-amber-100 text-amber-800 border-amber-300",
    "bg-teal-100 text-teal-800 border-teal-300",
    "bg-cyan-100 text-cyan-800 border-cyan-300",
    "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
];

const catColor = (key) => {
    let h = 0;
    for (let i = 0; i < (key || "").length; i++) h = (h * 31 + key.charCodeAt(i)) % FALLBACK_COLORS.length;
    return FALLBACK_COLORS[h] || FALLBACK_COLORS[0];
};

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; }
};

const truncate = (s, n) => {
    if (!s) return "";
    return s.length > n ? s.slice(0, n).trim() + "…" : s;
};

const plListings = (n) => {
    if (n === 1) return "ogłoszenie";
    const mod10 = n % 10, mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "ogłoszenia";
    return "ogłoszeń";
};

export default function OgloszeniaList({
    limit = null,
    category = null,
    showAddButton = true,
    pageSize = 12,
    showPagination = true,
}) {
    const [items, setItems] = useState([]);
    const [labels, setLabels] = useState({});
    const [ttlDays, setTtlDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);

    const fetchPage = useCallback(async (p) => {
        setLoading(true);
        const effectiveSize = limit || pageSize;
        const params = { page: p, page_size: effectiveSize };
        if (category) params.category = category;
        try {
            const [listRes, catRes] = await Promise.all([
                api.get("/listings", { params }),
                api.get("/listings/categories"),
            ]);
            const data = listRes.data;
            // backward compat: if API ever returns array, normalize
            if (Array.isArray(data)) {
                setItems(data);
                setTotal(data.length); setPages(1); setPage(1);
            } else {
                setItems(data.items || []);
                setTotal(data.total || 0);
                setPages(data.pages || 1);
                setPage(data.page || 1);
            }
            setLabels(catRes.data?.labels || {});
            setTtlDays(catRes.data?.ttl_days || 30);
        } catch { /* ignore */ } finally { setLoading(false); }
    }, [category, limit, pageSize]);

    useEffect(() => { fetchPage(1); }, [fetchPage]);

    const usePagination = showPagination && !limit && pages > 1;

    return (
        <div data-testid="ogloszenia-list">
            {showAddButton && (
                <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
                    {total > 0 && (
                        <p className="text-xs text-gray-500" data-testid="ogloszenia-total">
                            Łącznie: <strong>{total}</strong> {plListings(total)}
                        </p>
                    )}
                    <Link
                        to="/ogloszenia/dodaj"
                        data-testid="ogloszenia-add-link"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white text-sm font-medium ml-auto"
                    >
                        <Plus size={16} /> Dodaj ogłoszenie
                    </Link>
                </div>
            )}
            {loading && <p className="text-gray-500 text-sm">Wczytywanie...</p>}
            {!loading && items.length === 0 && (
                <p className="text-gray-500 text-sm" data-testid="ogloszenia-empty">
                    Brak aktualnych ogłoszeń. Bądź pierwszą osobą, która coś wystawi!
                </p>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 [&>*]:min-w-0">
                {items.map((it, idx) => (
                    <motion.article
                        key={it.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: idx * 0.04 }}
                        className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-[#D4A537]/40 hover:-translate-y-0.5 transition-all duration-300 min-w-0 flex flex-col"
                        data-testid={`ogloszenie-card-${it.id}`}
                    >
                        <div className="flex items-center justify-between gap-2 mb-3">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${catColor(it.category)} max-w-[60%] truncate`}>
                                <Tag size={10} className="shrink-0" /> <span className="truncate">{labels[it.category] || it.category}</span>
                            </span>
                            <span className="text-[10px] text-gray-400 inline-flex items-center gap-1 shrink-0">
                                <Clock size={10} /> {formatDate(it.created_at)}
                            </span>
                        </div>
                        <Link to={`/ogloszenie/${it.id}`} className="block group min-w-0">
                            <h3 className="font-serif text-lg text-[#092E22] leading-snug mb-1.5 group-hover:text-[#D4A537] transition-colors break-words line-clamp-2 [overflow-wrap:anywhere]">
                                {it.title}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed mb-3 break-words line-clamp-3 [overflow-wrap:anywhere]">{truncate(it.content, 140)}</p>
                        </Link>
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-50 mt-auto gap-3">
                            <span className="truncate min-w-0 flex-1">{it.contact_name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                                {it.contact_email && <Mail size={12} className="text-[#0D4F3C]" />}
                                {it.contact_phone && <Phone size={12} className="text-[#0D4F3C]" />}
                                {it.comments_count > 0 && (
                                    <span className="inline-flex items-center gap-1">
                                        <MessageSquare size={12} className="text-[#D4A537]" /> {it.comments_count}
                                    </span>
                                )}
                            </div>
                        </div>
                        {it.age_days >= ttlDays - 7 && (
                            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-3">
                                Ogłoszenie zostanie zarchiwizowane za {Math.max(0, ttlDays - it.age_days)} dni.
                            </p>
                        )}
                    </motion.article>
                ))}
            </div>

            {usePagination && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="ogloszenia-pagination">
                    <button
                        onClick={() => fetchPage(Math.max(1, page - 1))}
                        disabled={page <= 1 || loading}
                        data-testid="ogloszenia-prev"
                        className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white disabled:opacity-40 hover:bg-[#FAF8F3] transition-colors"
                    >
                        ‹ Poprzednia
                    </button>
                    <span className="text-sm text-gray-600 px-3" data-testid="ogloszenia-page-label">
                        Strona <strong>{page}</strong> z <strong>{pages}</strong>
                    </span>
                    <button
                        onClick={() => fetchPage(Math.min(pages, page + 1))}
                        disabled={page >= pages || loading}
                        data-testid="ogloszenia-next"
                        className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white disabled:opacity-40 hover:bg-[#FAF8F3] transition-colors"
                    >
                        Następna ›
                    </button>
                </div>
            )}
        </div>
    );
}
